import { ChevronLeft as ChevronLeftIcon, X as XIcon } from "@tamagui/lucide-icons";
import { Button, CenterContent, GrantedTextLogo, type GrantedTextLogoTone, HeaderContainer, HeaderSide, isWeb, ProgressBar, VisuallyHidden, YStack } from "@medbillai/ui";

// Lifted from grantedhealth.com so onboarding and the marketing site read as
// one brand. The spacer opposite the mark tracks the same numbers, or the
// progress bar stops being centred.
const WORDMARK_MOBILE = {
  width: 90,
  height: 18
};
const WORDMARK_DESKTOP = {
  width: 110,
  height: 22
};

/** The Granted wordmark at the size the onboarding nav draws it. */
export function OnboardingWordmark({
  tone,
  testID
}: {
  tone: GrantedTextLogoTone;
  testID: string;
}) {
  return (
    // Sized on the parent because the mark is an SVG and takes no style
    // props of its own.
    <YStack flexShrink={0} testID={testID} {...WORDMARK_MOBILE} $gtSm={WORDMARK_DESKTOP} data-sentry-element="YStack" data-sentry-component="OnboardingWordmark" data-sentry-source-file="OnboardingProgressHeader.tsx">
      <GrantedTextLogo width="100%" height="100%" tone={tone} data-sentry-element="GrantedTextLogo" data-sentry-source-file="OnboardingProgressHeader.tsx" />
    </YStack>
  );
}
type OnboardingProgressHeaderProps = {
  /**
   * Progress value between 0 and 100. When omitted, the progress bar is
   * hidden; the rest of the header (wordmark or back button) still renders.
   */
  progress?: number;
  /**
   * Native only — when provided, shows a back button on the left and wires it
   * to this handler. Web uses the browser's back button for navigation.
   */
  onBackPress?: () => void;
  /** Accessible title. Not rendered visibly; used for screen-reader context. */
  title?: string;
  /** Web only — render the Granted wordmark in the left slot. @default true */
  showLogo?: boolean;
  /**
   * Dark screens render a white wordmark and inverse nav icons. Set by
   * `OnboardingScreen`'s `tone`; callers rarely pass it directly.
   * @default "light"
   */
  tone?: "light" | "dark";
  /** When provided, shows a close button in the trailing slot. */
  onClosePress?: () => void;
};

/**
 * Onboarding-flow header. Uses styles from `NavHeader`'s progressBar layout but
 * doesnt reuse the component so the progress bar can be optional without touching the shared `NavHeader`.
 *
 * On web: Granted wordmark on the left, optional progress bar centered, a
 * symmetric spacer on the right to keep the progress bar visually centered.
 * Onboarding on web never shows a back button.
 *
 * On native: standard back-button layout (like `NavHeader` progressBar), with
 * the progress bar optional. Both platforms show a close button in the
 * trailing slot only when `onClosePress` is given — onboarding flows that
 * can't be abandoned part-way leave it off.
 */
export function OnboardingProgressHeader({
  progress,
  onBackPress,
  title = "Onboarding",
  showLogo = true,
  tone = "light",
  onClosePress
}: OnboardingProgressHeaderProps) {
  const isDark = tone === "dark";
  const closeButton = onClosePress ? <Button variant="tertiary" size="large" tone={tone} icon={XIcon} aria-label="Close" onPress={onClosePress}
  // Offset button padding so the icon aligns to the content edge.
  marginRight="$-sp2" /> : null;
  const progressCenter = progress !== undefined ? <ProgressBar value={progress} width="$si10" tone={tone} /> : null;
  if (isWeb) {
    return <HeaderContainer>
        <VisuallyHidden>{title}</VisuallyHidden>
        {showLogo ? <OnboardingWordmark tone={isDark ? "inverseSubtle" : "subtle"} testID={`onboarding-nav-wordmark-${tone}`} /> : <YStack flexShrink={0} width={WORDMARK_MOBILE.width} $gtSm={{
        width: WORDMARK_DESKTOP.width
      }} />}
        <CenterContent>{progressCenter}</CenterContent>
        <YStack flexShrink={0} alignItems="flex-end" width={WORDMARK_MOBILE.width} $gtSm={{
        width: WORDMARK_DESKTOP.width
      }}>
          {closeButton}
        </YStack>
      </HeaderContainer>;
  }
  return <HeaderContainer data-sentry-element="HeaderContainer" data-sentry-component="OnboardingProgressHeader" data-sentry-source-file="OnboardingProgressHeader.tsx">
      <VisuallyHidden data-sentry-element="VisuallyHidden" data-sentry-source-file="OnboardingProgressHeader.tsx">{title}</VisuallyHidden>
      <HeaderSide data-sentry-element="HeaderSide" data-sentry-source-file="OnboardingProgressHeader.tsx">
        {onBackPress ? <Button variant="tertiary" size="large" tone={tone} icon={ChevronLeftIcon} aria-label="Go back" onPress={onBackPress} /> : null}
      </HeaderSide>
      <CenterContent data-sentry-element="CenterContent" data-sentry-source-file="OnboardingProgressHeader.tsx">{progressCenter}</CenterContent>
      <HeaderSide data-sentry-element="HeaderSide" data-sentry-source-file="OnboardingProgressHeader.tsx">{closeButton}</HeaderSide>
    </HeaderContainer>;
}